import axios from "axios";

import { setCarrierSettingPopup, setTerm } from "../actions/user";
import { api } from "../config/variables";
import store from "../store/storeInstance";
import { readCookie } from "../utils/functions";
import { redirectIfAuthExpired } from "../utils/handleAuthExpiry";

export const axiosInstance = axios.create({
	baseURL: api.baseURL + api.restApiRoot,
	headers: {
		"Content-Type": "application/json;charset=UTF-8",
	},
	crossDomain: true,
	withCredentials: true,
});

export const axiosRegistration = axios.create({
	baseURL: api.baseURL,
	headers: {
		"Content-Type": "application/json;charset=UTF-8",
	},
	crossDomain: true,
	withCredentials: true,
});

export const axiosRatingInstance = axios.create({
	baseURL:
		api.other_services.rating.baseURL + api.other_services.rating.restApiRoot,
});

export const axiosFastApiInstance = axios.create({
	baseURL: api.fast_api,
	crossDomain: true,
	headers: {
		"Content-Type": "application/json;charset=UTF-8",
	},
	withCredentials: true,
});

axiosFastApiInstance.interceptors.request.use(
	(config) => {
		const bearerToken = readCookie("bearer");
		if (bearerToken) {
			return {
				...config,
				headers: {
					...config.headers,
					Authorization: /^bearer\s+/i.test(bearerToken)
						? bearerToken
						: `Bearer ${bearerToken}`,
				},
			};
		}
		return config;
	},
	(error) => Promise.reject(error)
);

export const axiosShipRocketInstance = axios.create({
	baseURL: api.shiprocket + api.shiprocket_version,
	crossDomain: true,
	headers: {
		"Content-Type": "application/json;charset=UTF-8",
	},
});

axiosShipRocketInstance.interceptors.request.use(
	(config) => {
		const bearerToken = readCookie("bearer");
		if (bearerToken) {
			return {
				...config,
				headers: {
					...config.headers,
					Authorization: /^bearer\s+/i.test(bearerToken)
						? bearerToken
						: `Bearer ${bearerToken}`,
				},
			};
		}
		return config;
	},
	(error) => Promise.reject(error)
);

export const axiosShipRocketKycInstance = axios.create({
	baseURL: api.shiprocketKyc + api.shiprocket_version,
	crossDomain: true,
	headers: {
		"Content-Type": "application/json;charset=UTF-8",
	},
});

axiosShipRocketKycInstance.interceptors.request.use(
	(config) => {
		const bearerToken = readCookie("bearer");
		if (bearerToken) {
			return {
				...config,
				headers: {
					...config.headers,
					Authorization: /^bearer\s+/i.test(bearerToken)
						? bearerToken
						: `Bearer ${bearerToken}`,
				},
			};
		}
		return config;
	},
	(error) => Promise.reject(error)
);

axiosInstance.interceptors.request.use(
	(config) => {
		const token = readCookie("bearer");
		if (token) {
			return {
				...config,
				headers: {
					...config.headers,
					Authorization: /^bearer\s+/i.test(token) ? token : `Bearer ${token}`,
				},
			};
		}
		return config;
	},
	(err) => Promise.reject(err)
);

axiosInstance.interceptors.response.use(
	(response) => {
		store.dispatch(
			setTerm({
				maintenance_popup: false,
			})
		);
		return response;
	},
	(err) => {
		if (err?.response?.status > 500) {
			store.dispatch(
				setTerm({
					maintenance_popup: true,
				})
			);
		}
		return Promise.reject(err);
	}
);

axiosInstance.interceptors.response.use(
	(response) => {
		store.dispatch(
			setTerm({
				agreement_popup: false,
			})
		);

		return response;
	},
	(err) => {
		if (
			err?.response?.status === 403 &&
			err?.response?.data?.detail ===
				"Please accept agreement terms to proceed further"
		) {
			store.dispatch(
				setTerm({
					agreement_popup: true,
					agreement_popup_data: err.response.data,
				})
			);
			return Promise.reject(err || new Error("Agreement terms required"));
		}
		if (
			err?.response?.status === 403 &&
			err?.response?.data?.detail === "Auto Assign Carrier is required"
		) {
			localStorage.setItem("client_id", err?.response?.data?.client_id);
			store.dispatch(
				setCarrierSettingPopup({
					carrier_setting_required: true,
				})
			);

			return Promise.reject(
				err || new Error("Auto Assign Carrier is required")
			);
		}
		return Promise.reject(err);
	}
);

axiosRatingInstance.interceptors.request.use(
	(config) => {
		const token = readCookie("bearer");
		if (token) {
			return {
				...config,
				headers: {
					...config.headers,
					"Content-Type": "application/json",
					Authorization: /^bearer\s+/i.test(token) ? token : `Bearer ${token}`,
				},
			};
		}
		return {
			...config,
			headers: {
				...config.headers,
				"Content-Type": "application/json",
			},
		};
	},
	(err) => Promise.reject(err)
);

/**
 * Surfaces mounted OUTSIDE `PrivateRoute`, which therefore have to recover from
 * an expired session by themselves.
 *
 * The legacy recovery every thunk inlines — `reloadUser()` then
 * `window.location.reload()` — is only half a recovery. The half that actually
 * rescues the user lives in `PrivateRoute` (Authorization.jsx), which finds no
 * bearer cookie after the reload and does `window.location.replace(authUrl)`.
 * `/depot-connect-v2` is a plain `<Route>` in index.jsx, a sibling of the
 * `<PrivateRoute>` blocks rather than one of them, so that second half never
 * runs there: the first half erases the session and reloads the user onto the
 * same dead screen, where every request fails and nothing leads back to login.
 *
 * Scoped to that route on purpose. Under `PrivateRoute` an expired session is
 * already handled better than a redirect could: ~87 call sites across apilist/
 * and features/ set `showReAuthModal: true` on a 401 WITHOUT reloading, which
 * renders ReAuthModal (Authorization.jsx), opens SSO in a NEW tab and leaves
 * the current page — and any half-filled form — alive. AuthConfirm then replays
 * `shipment.replayRequest.razorPayOrderRequest` and closes that tab. Firing a
 * whole-tab redirect there would destroy in-progress work and break the
 * RazorPay replay, so on every other path this interceptor is a pass-through.
 *
 * `/client/seller/complete-profile` is the second such route, for a different
 * reason. It sits nominally under `PrivateRoute`, but Authorization.jsx exempts
 * it from the bearer-token check via `isSellerProfileRoute`, so a cookie-less
 * visitor is rendered the whole onboarding flow instead of being sent to login.
 * This interceptor is therefore the ONLY thing that ejects them.
 *
 * It works because the route fires authenticated calls on mount, not just at
 * submit: OnBoardingFlow renders both StepPanes at once, so PersonalForm /
 * BusinessForm are live at step 1 with `termsAccepted` defaulting to true,
 * firing `userAgreement()`, while `trackEvent(ONBOARDING_FORM_VISIT)` fires
 * `postGaEvent` alongside it. Both go through `axiosInstance`, both 403 with
 * "Authentication credentials were not provided.", and `isAuthExpired` matches
 * that on the detail string rather than the status code — so the redirect fires
 * without waiting for the user to fill anything in.
 *
 * Two consequences worth knowing before changing anything here. The ejection is
 * asynchronous, so the form stays interactive for a full request round trip
 * plus CORS preflight; and it depends on those mount-time calls continuing to
 * exist — lazy-mounting the second StepPane, or defaulting the T&C box to
 * unchecked, would silently strand unauthenticated visitors on the form again
 * with no test failing. Restoring the guard in Authorization.jsx is the
 * durable fix; this is the one that works without touching the app-wide gate.
 *
 * The ReAuthModal recovery described above does not apply here either —
 * `postSignupProfileInfo` is not one of the ~87 call sites that set
 * `showReAuthModal`, so nothing opens a second tab, and the only thing a
 * redirect discards is a name/email the user must re-enter after login anyway.
 */
const GUARDLESS_ROUTES =
	/^\/(?:depot-connect-v2|client\/seller\/complete-profile)/;

/**
 * Turns an expired session into a redirect to login on guard-less routes.
 *
 * Returns a promise that never settles once the redirect is committed. That is
 * load-bearing, not a shortcut: it starves the caller's catch block, so the
 * legacy `window.location.reload()` still present in the thunks cannot run and
 * cancel the `window.location.replace(loginUrl)` already in flight — which on a
 * route with no guard above it would otherwise loop. Navigation does not halt
 * the current tick, so anything left runnable after this point would still
 * execute, and an error toast painted on the way out is exactly the raw-envelope
 * noise `handleAuthExpiry` exists to remove.
 */
const authExpiryInterceptor = (err) => {
	if (!GUARDLESS_ROUTES.test(window.location.pathname)) {
		return Promise.reject(err);
	}
	if (redirectIfAuthExpired(err)) {
		return new Promise(() => {});
	}
	return Promise.reject(err);
};

[axiosInstance, axiosFastApiInstance, axiosShipRocketInstance].forEach(
	(instance) => {
		instance.interceptors.response.use(
			(response) => response,
			authExpiryInterceptor
		);
	}
);
